import { getBuddyUserByID } from '@adornis/bh-matching-user/api/MatchingUserAPI.js';
import { logger } from '@adornis/base/logging.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { runInServerContext } from '@adornis/baseql/server/server.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { matchingConfig } from '@adornis/bh-matching-config/config.js';
import { addCommentOnMatch, onMatchCreated, getMatchByID } from '@adornis/bh-matching-matches/api/matchAPI.js';
import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-matching-user/checkAuthorizedMiddleware.js';
import { MatchingPermission } from '@adornis/bh-matching-user/permissions.js';
import { getUserClasses } from '@adornis/bh-matching-user/user-class.js';
import { mailer } from '@adornis/buchungsheld-db/mails/communication.js';
import { getMailTemplateByType } from '@adornis/mails/db/mail-template.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { switchMap } from 'rxjs';
import { BuddyMailTemplate, MailTemplateType } from '../entities/mail-templates.js';
import { mmr } from '../mail-registry.js';

export async function sendMatchCreationMails(match: Pick<Match, '_id' | 'mentorID' | 'menteeID'>) {
  return runInServerContext(async () => {
    const mentorTemplate = await getMailTemplateByType(MailTemplateType.MATCHING_MENTOR)(
      selectionSet(() => BuddyMailTemplate, 1),
    );
    const menteeTemplate = await getMailTemplateByType(MailTemplateType.MATCHING_MENTEE)(
      selectionSet(() => BuddyMailTemplate, 1),
    );

    if (!mentorTemplate)
      return logger.error(
        {
          mailTemplateType1: MailTemplateType.MATCHING_MENTEE,
          mailTemplateType2: MailTemplateType.MATCHING_MENTOR,
          mentorTemplate: !!mentorTemplate,
          menteeTemplate: !!menteeTemplate,
        },
        'Couldnt find buddy mail template',
      );

    const userClasses = getUserClasses();
    if (!match.mentorID || !match.menteeID)
      throw new Error('match IDs missing, this state should not be allowed to exist at this point');
    const mentor = await getBuddyUserByID(
      userClasses.mentor._class,
      match.mentorID,
    )(selectionSet(() => userClasses.mentor, 2));
    const mentee = await getBuddyUserByID(
      userClasses.mentee._class,
      match.menteeID,
    )(selectionSet(() => userClasses.mentee, 2));

    if (!mentor || !mentee)
      return logger.error(
        { mentor: !!mentor, mentee: !!mentee, matchID: match._id },
        'Couldnt find mentor or mentee for match mail',
      );

    try {
      const _mailer = await mailer;
      await mmr.matchingMentee(match._id, MailTemplateType.MATCHING_MENTEE).send({
        mailer: _mailer,
        recipients: [
          new Recipient(mentee.email, matchingConfig.get('DIRECT_MAIL_COPY_ADDRESS') ?? undefined, undefined),
        ],
      });
      await mmr.matchingMentor(match._id, MailTemplateType.MATCHING_MENTOR).send({
        mailer: _mailer,
        recipients: [
          new Recipient(mentor.email, matchingConfig.get('DIRECT_MAIL_COPY_ADDRESS') ?? undefined, undefined),
        ],
      });
    } catch (err) {
      logger.error({ err }, 'Couldnt send match mail to user');
    }
  });
}

export const sendReminderMailForMatch = registerMutation({
  operationName: 'sendReminderMailForMatch',
  middlewares: [
    checkAuthorizedMiddleware(
      ({ user, params }) =>
        user._id === params.userID || user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_MATCHES),
    ),
  ],
  resolve: (matchID, userID) => () =>
    runInServerContext(async () => {
      const match = await getMatchByID(matchID)({ mentorID: 1, menteeID: 1 });
      if (!match) throw new Error('No match found for ID ' + matchID);
      if (!match.mentorID || !match.menteeID)
        throw new Error(
          `Incomplete match found (matchID: ${matchID}; mentorID: ${match.mentorID}; menteeID: ${match.menteeID})`,
        );
      if (match.mentorID !== userID && match.menteeID !== userID)
        throw new Error(`UserID is not part of this match (userID: ${userID}; matchID: ${matchID})`);

      const userClasses = getUserClasses();

      const mentor = await getBuddyUserByID(
        userClasses.mentor._class,
        match.mentorID,
      )(selectionSet(() => userClasses.mentor, 2));
      const mentee = await getBuddyUserByID(
        userClasses.mentee._class,
        match.menteeID,
      )(selectionSet(() => userClasses.mentee, 2));
      if (!mentor || !mentee)
        throw new Error(
          `Couldnt find mentor or mentee for match (mentor: ${!!mentor}; mentee: ${!!mentee}; matchID: ${matchID})`,
        );

      const isMentor = match.mentorID === userID;

      const recipients = [
        new Recipient(
          isMentor ? mentor.email : mentee.email,
          matchingConfig.get('DIRECT_MAIL_COPY_ADDRESS') ?? undefined,
          undefined,
        ),
      ];
      const options = { recipients, mailer: await mailer };
      const template = isMentor
        ? mmr.contactReminderMentor(matchID, MailTemplateType.CONTACT_REMINDER_MENTOR)
        : mmr.contactReminderMentee(matchID, MailTemplateType.CONTACT_REMINDER_MENTEE);

      await template.send(options);

      await addCommentOnMatch(matchID, `Erinnerungsmail wurde versandt für ${isMentor ? 'Mentor' : 'Mentee'}`)().catch(
        err => {
          logger.error({ err });
          // no further consequences, this is just an information
        },
      );
    }),
  type: () => Boolean,
  params: [
    { name: 'matchID', type: () => String },
    { name: 'userID', type: () => String },
  ],
});

onMatchCreated
  .pipe(
    switchMap(id =>
      runInServerContext(async () => {
        const match = await getMatchByID(id)({ _id: 1, mentorID: 1, menteeID: 1 });
        if (!match) throw new Error('No match found for ID ' + id);
        await sendMatchCreationMails(match);
      }),
    ),
  )
  .subscribe(() => {});
